import MakedoConfig from './_config.js';
import Fetch from './fetch.js';
import { MakedoError, ErrorCodes } from './errors.js';

const uuid = () => {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
    (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
  );
}

// all properties in pattern must be present and match in message
const haveMatchingProperties = ({pattern, message}) => {
  let misses = 0;
  Object.keys(pattern).forEach((prop) => {
    if(pattern[prop] !== message[prop]) misses++;
  })
  return misses === 0;
}

const WS = { 
  /**
   * Creates a WebSocket connection with post-connect authentication.
   * Security: Token is sent via message after connection, not in URL.
   * @param {string} serverRoute - WebSocket server URL
   * @param {function} onMessageSpecific - Message handler callback
   * @param {function} onAuthSuccess - Called when authentication succeeds
   * @param {function} onAuthError - Called when authentication fails
   * @returns {WebSocket} The WebSocket instance
   */
  getWebsocket(serverRoute, onMessageSpecific, onAuthSuccess, onAuthError, onDisconnect) {
    if(!serverRoute) return null;
    
    // No longer add authToken to URL - will send via message after connection
    let ws = new WebSocket(serverRoute);
    let isAuthenticated = false;
    const messageBuffer = [];
    
    ws.onopen = function() {
      console.debug("WEBSOCKET is open now. Sending authentication...");
      // Send auth token as first message after connection
      if (Fetch.authToken) {
        ws.send(JSON.stringify({ type: 'auth', token: Fetch.authToken }));
      } else {
        console.warn('WebSocket opened but no auth token available — push notifications will not work.');
        const err = new MakedoError(ErrorCodes.NETWORK_WS_AUTH_FAILED, 'No auth token available for WebSocket authentication', { context: 'websocket.onopen', retriable: false });
        if (onAuthError) onAuthError(err);
      }
    };
    
    ws.onmessage = function(event) {
      const data = event.data;
      try {
        const message = JSON.parse(data);
        
        // Handle authentication response
        if (message.type === 'auth_success') {
          console.debug("WEBSOCKET authenticated successfully");
          isAuthenticated = true;
          if (onAuthSuccess) onAuthSuccess(message);
          // Process any buffered messages
          while (messageBuffer.length > 0) {
            const bufferedData = messageBuffer.shift();
            onMessageSpecific(bufferedData);
          }
          return;
        }
        
        if (message.type === 'auth_error') {
          const err = new MakedoError(ErrorCodes.NETWORK_WS_AUTH_FAILED, message.error || 'WebSocket authentication failed', { context: 'websocket.onmessage', retriable: true });
          if (onAuthError) onAuthError(err);
          return;
        }
        
        // Buffer messages until authenticated
        if (!isAuthenticated) {
          console.debug("WEBSOCKET: Buffering message until authenticated");
          messageBuffer.push(data);
          return;
        }
        
        // Normal message processing
        onMessageSpecific(data);
      } catch (e) {
        // Non-JSON message, pass through if authenticated
        if (isAuthenticated) {
          onMessageSpecific(data);
        } else {
          messageBuffer.push(data);
        }
      }
    };
    
    ws.onclose = function(event) {
      const wasClean = event.wasClean;
      if (!wasClean && onDisconnect) {
        onDisconnect(new MakedoError(ErrorCodes.NETWORK_WS_DISCONNECTED, `WebSocket closed unexpectedly (code ${event.code})`, { context: 'websocket.onclose', retriable: true }));
      }
    };
    
    // Expose authentication state
    ws.isAuthenticated = () => isAuthenticated;
    
    return ws;
  }
}

class PubSub {
  constructor(){
    this.socket = null;
    this.subscriptions = [];
    this.isAuthenticated = false;
    this.authPromise = null;
    this.authResolve = null;
    this.authReject = null;
    this.onError = null;  // Set by Bridge to receive WS errors as MakedoError
    this.handleMessage = this.handleMessage.bind(this);
    
    // Create a promise that resolves when authenticated
    this.authPromise = new Promise((resolve, reject) => {
      this.authResolve = resolve;
      this.authReject = reject;
    });
    // Prevent "Uncaught (in promise)" — errors are already routed via onError callback
    this.authPromise.catch(() => {});
    
    // Initialize WebSocket connection after setting up properties
    this.init();
  }
  
  async init() {
    try {
      const wsRoute = MakedoConfig.websocketServerRoute;
      if (!wsRoute) {
        const err = new MakedoError(ErrorCodes.NETWORK_WS_AUTH_FAILED, 'WebSocket not initialized: websocketServerRoute not configured', { context: 'PubSub.init', retriable: false });
        this.authReject && this.authReject(err);
        return;
      }
      this.socket = WS.getWebsocket(
        wsRoute, 
        this.handleMessage,
        // onAuthSuccess callback
        (message) => {
          this.isAuthenticated = true;
          this.authResolve && this.authResolve(message);
        },
        // onAuthError callback
        (error) => {
          this.isAuthenticated = false;
          this.authReject && this.authReject(error);
          if (this.onError) this.onError(error);
        },
        // onDisconnect callback
        (error) => {
          this.isAuthenticated = false;
          if (this.onError) this.onError(error);
        }
      );
    } catch (error) {
      const err = (error instanceof MakedoError) ? error : new MakedoError(ErrorCodes.NETWORK_WS_DISCONNECTED, error.message || 'WebSocket connection failed', { cause: error, context: 'PubSub.init', retriable: true });
      this.authReject && this.authReject(err);
    }
  }
  
  // Wait for authentication to complete
  async waitForAuth() {
    return this.authPromise;
  }

  handleMessage(m){
    const compare = {};
    let message;
    try {
      message = JSON.parse(m);
    } catch (e) {
      console.warn('[VibeLive] WebSocket received non-JSON message, ignoring:', e.message);
      return;
    }
    
    // DEBUG: WebSocket message logging removed for production
    // To re-enable: console.debug('WS ←', message.type, message.command);
    
    if(message.system) return;
    // Create an object to use for comparison
    ['type', 'member_id', 'channel_id', 'user_id'].forEach((prop) => {
      if(message[prop]) compare[prop] = message[prop];
    })
    this.subscriptions.forEach((s) => {
      try {
        if (compare == {} || haveMatchingProperties({pattern:s.pattern, message:compare})){
          s.callback(message);
        }
      } catch(err) {
        console.error('Error in subscription callback:', err);
      }
    })
  }

  // pattern is a hash {type:'channel', channel_id:'C1211212', member_id:'M121212121', user_id:'U121212121'}
  on({pattern, callback}){
    if(!pattern || !callback) return;
    const id = uuid();
    this.subscriptions.push({pattern, callback, id});
    return id;
  }

  // Alias for on() - subscribe to messages matching a pattern
  subscribe(eventType, callback) {
    return this.on({ pattern: { type: eventType }, callback });
  }

  // Send a message to the server via WebSocket
  send(payload) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN && this.isAuthenticated) {
      this.socket.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }

  // Publish a message to all matching subscribers (for internal routing)
  publish(eventType, message) {
    const publishMessage = { ...message, type: eventType };
    this.subscriptions.forEach((s) => {
      try {
        const compare = {};
        ['type', 'member_id', 'channel_id', 'user_id'].forEach((prop) => {
          if(publishMessage[prop]) compare[prop] = publishMessage[prop];
        });
        
        if (haveMatchingProperties({pattern: s.pattern, message: compare})){
          s.callback(publishMessage);
        }
      } catch(err) {
        console.error('Error in publish callback:', err);
      }
    });
  }

  disconnect(uuid){
    this.subscriptions = this.subscriptions.filter((s) => s.id !== uuid);
  }

}

// Export the class, not an instance - instance will be created only when user is logged in
export { PubSub };